import { useState } from 'react'

const initialEmails = [
  {
    id: 1,
    folder: 'inbox',
    sender: 'Pink Pixel Admin',
    email: 'admin@pinkpixel.dev',
    subject: 'Welcome to Pink Pixel OS v2.3',
    preview: 'Welcome to your personalized desktop environment! Explore the portfolio, Crumb AI, and tools.',
    body: `Hey there,\n\nWelcome to Pink Pixel OS! This environment brings together your tools, live weather, music, and portfolio projects in a simulated desktop experience.\n\nKey areas you can explore:\n- Chat with Crumb, your AI cupcake assistant\n- Browse local projects inside the File Explorer\n- Listen to curated beats through the floating music player\n- Play Tetris and Snake in the Game Center\n\nEnjoy exploring, and dream it, pixel it!\n\nBest,\nPink Pixel Team`,
    time: '14:20',
    date: 'Today',
    read: false,
    starred: true
  },
  {
    id: 2,
    folder: 'inbox',
    sender: 'GitHub Notifications',
    email: 'notifications@github.com',
    subject: 'Pull request merged: AI Tools & MCP integration',
    preview: 'PR #42 "Add unified MCP tools server" was merged into dev by pinkpixel-dev.',
    body: `Your pull request #42 "Add unified MCP tools server" has been successfully reviewed and merged into the dev branch.\n\nAll automated checks passed:\n- Unit tests: 24/24 passed\n- Linting: 0 warnings\n- Bundle size: within limits\n\nThanks for your contribution!`,
    time: '12:45',
    date: 'Today',
    read: true,
    starred: false
  },
  {
    id: 3,
    folder: 'inbox',
    sender: 'Cloudflare Pages',
    email: 'alerts@cloudflare.com',
    subject: 'Deployment successful: pink-pixel-os production',
    preview: 'Your deployment to pinkpixel.dev completed in 42 seconds with zero errors.',
    body: `Deployment Details:\n- Project: pink-pixel-os\n- Environment: Production\n- Branch: main\n- URL: https://pinkpixel.dev\n- Build duration: 42s\n\nYour changes are now live globally across Cloudflare's edge network.`,
    time: 'Yesterday',
    date: 'Sep 5',
    read: true,
    starred: true
  },
  {
    id: 4,
    folder: 'sent',
    sender: 'You',
    email: 'user@pinkpixel.dev',
    subject: 'Feedback on desktop window controls',
    preview: 'The new pointer-driven resize handles work great on mobile touchscreens.',
    body: `Hey team,\n\nJust tested the new pointer-driven resize handles on mobile. They feel much smoother and do not fight the default scrolling gestures anymore.\n\nLooking forward to the next update!`,
    time: 'Sep 4',
    date: 'Sep 4',
    read: true,
    starred: false
  }
]

const MailApp = () => {
  const [emails, setEmails] = useState(initialEmails)
  const [activeFolder, setActiveFolder] = useState('inbox')
  const [selectedEmailId, setSelectedEmailId] = useState(1)
  const [isComposing, setIsComposing] = useState(false)
  const [composeTo, setComposeTo] = useState('')
  const [composeSubject, setComposeSubject] = useState('')
  const [composeBody, setComposeBody] = useState('')
  const [toastMessage, setToastMessage] = useState(null)
  const [mobileShowDetail, setMobileShowDetail] = useState(false)

  const showToast = (msg) => {
    setToastMessage(msg)
    setTimeout(() => setToastMessage(null), 3000)
  }

  const filteredEmails = emails.filter(email => {
    if (activeFolder === 'important') return email.starred
    return email.folder === activeFolder
  })

  const selectedEmail = emails.find(e => e.id === selectedEmailId) || filteredEmails[0]

  const handleSelectEmail = (email) => {
    setSelectedEmailId(email.id)
    setMobileShowDetail(true)
    if (!email.read) {
      setEmails(prev => prev.map(e => e.id === email.id ? { ...e, read: true } : e))
    }
  }

  const toggleStar = (id, e) => {
    e?.stopPropagation()
    setEmails(prev => prev.map(e => e.id === id ? { ...e, starred: !e.starred } : e))
  }

  const handleDelete = (id) => {
    setEmails(prev => prev.filter(e => e.id !== id))
    showToast('Message deleted')
    setMobileShowDetail(false)
  }

  const handleSend = (e) => {
    e.preventDefault()
    if (!composeTo || !composeSubject) {
      showToast('Please specify recipient and subject')
      return
    }

    const newEmail = {
      id: Date.now(),
      folder: 'sent',
      sender: 'You',
      email: 'user@pinkpixel.dev',
      subject: composeSubject,
      preview: composeBody.slice(0, 80),
      body: composeBody,
      time: 'Just now',
      date: 'Today',
      read: true,
      starred: false
    }

    setEmails(prev => [newEmail, ...prev])
    setIsComposing(false)
    setComposeTo('')
    setComposeSubject('')
    setComposeBody('')
    showToast('Message sent successfully')
  }

  const handleReply = () => {
    if (!selectedEmail) return
    setComposeTo(selectedEmail.email)
    setComposeSubject(`Re: ${selectedEmail.subject}`)
    setComposeBody(`\n\n--- On ${selectedEmail.date}, ${selectedEmail.sender} wrote:\n> ${selectedEmail.body.split('\n').join('\n> ')}`)
    setIsComposing(true)
  }

  const unreadCount = emails.filter(e => e.folder === 'inbox' && !e.read).length

  return (
    <div className="flex h-full bg-slate-950/40 text-white overflow-hidden relative">
      {/* Toast alert */}
      {toastMessage && (
        <div className="absolute top-4 right-4 z-50 bg-pink-600/90 text-white px-4 py-2 rounded-lg text-xs shadow-lg backdrop-blur-md transition-all">
          {toastMessage}
        </div>
      )}

      {/* Sidebar navigation */}
      <div className="w-16 sm:w-48 bg-white/5 border-r border-white/10 p-2 sm:p-3 flex flex-col justify-between flex-shrink-0">
        <div>
          <button
            onClick={() => setIsComposing(true)}
            className="w-full bg-pink-500/20 hover:bg-pink-500/30 text-pink-300 border border-pink-400/30 rounded-xl py-2 px-3 mb-4 text-xs font-medium flex items-center justify-center gap-2 transition-colors"
          >
            <i className="fas fa-pen text-xs"></i>
            <span className="hidden sm:inline">New Message</span>
          </button>

          <nav className="space-y-1">
            <button
              onClick={() => { setActiveFolder('inbox'); setMobileShowDetail(false); }}
              className={`w-full text-left p-2 rounded-lg text-xs font-medium flex items-center justify-between transition-colors ${
                activeFolder === 'inbox' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/5'
              }`}
            >
              <div className="flex items-center gap-2">
                <i className="fas fa-inbox text-blue-400"></i>
                <span className="hidden sm:inline">Inbox</span>
              </div>
              {unreadCount > 0 && (
                <span className="bg-pink-500/40 text-pink-200 text-[10px] px-1.5 py-0.2 rounded-full font-bold">
                  {unreadCount}
                </span>
              )}
            </button>

            <button
              onClick={() => { setActiveFolder('sent'); setMobileShowDetail(false); }}
              className={`w-full text-left p-2 rounded-lg text-xs font-medium flex items-center gap-2 transition-colors ${
                activeFolder === 'sent' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/5'
              }`}
            >
              <i className="fas fa-paper-plane text-emerald-400"></i>
              <span className="hidden sm:inline">Sent</span>
            </button>

            <button
              onClick={() => { setActiveFolder('important'); setMobileShowDetail(false); }}
              className={`w-full text-left p-2 rounded-lg text-xs font-medium flex items-center gap-2 transition-colors ${
                activeFolder === 'important' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/5'
              }`}
            >
              <i className="fas fa-star text-amber-400"></i>
              <span className="hidden sm:inline">Important</span>
            </button>
          </nav>
        </div>

        <div className="text-[10px] text-white/40 text-center sm:text-left pt-2 border-t border-white/10 hidden sm:block">
          Pink Pixel Mail v2.3
        </div>
      </div>

      {/* Main Mail Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Email list column */}
        <div className={`${mobileShowDetail ? 'hidden md:flex' : 'flex'} w-full md:w-80 flex-col border-r border-white/10 bg-black/20 flex-shrink-0`}>
          <div className="p-3 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-sm font-semibold capitalize flex items-center gap-2">
              {activeFolder}
            </h3>
            <span className="text-xs text-white/50">{filteredEmails.length} messages</span>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
            {filteredEmails.length === 0 ? (
              <div className="py-12 text-center text-xs text-white/40">
                No messages in this folder
              </div>
            ) : (
              filteredEmails.map((email) => (
                <div
                  key={email.id}
                  onClick={() => handleSelectEmail(email)}
                  className={`p-3 rounded-xl cursor-pointer transition-all border ${
                    selectedEmail?.id === email.id
                      ? 'bg-white/15 border-pink-400/40 text-white'
                      : 'bg-white/5 hover:bg-white/10 border-white/5 text-white/80'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-xs truncate font-medium ${!email.read ? 'text-pink-300 font-bold' : ''}`}>
                      {email.sender}
                    </span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-[10px] text-white/50">{email.time}</span>
                      <button
                        onClick={(e) => toggleStar(email.id, e)}
                        className="text-white/40 hover:text-amber-400 transition-colors"
                      >
                        <i className={`${email.starred ? 'fas fa-star text-amber-400' : 'far fa-star'} text-xs`}></i>
                      </button>
                    </div>
                  </div>
                  <p className={`text-xs truncate ${!email.read ? 'text-white font-semibold' : 'text-white/90'}`}>
                    {email.subject}
                  </p>
                  <p className="text-[11px] text-white/50 line-clamp-1 mt-0.5">
                    {email.preview}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Email reader detail view */}
        <div className={`${!mobileShowDetail ? 'hidden md:flex' : 'flex'} flex-1 flex-col bg-black/10 overflow-hidden`}>
          {selectedEmail ? (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Toolbar */}
              <div className="p-3 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setMobileShowDetail(false)}
                    className="md:hidden px-2 py-1 bg-white/10 rounded text-xs text-white"
                  >
                    ← Back
                  </button>
                  <button
                    onClick={handleReply}
                    className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-lg text-xs flex items-center gap-1.5 transition-colors"
                  >
                    <i className="fas fa-reply text-xs"></i>
                    Reply
                  </button>
                  <button
                    onClick={() => toggleStar(selectedEmail.id)}
                    className="p-1 px-2 hover:bg-white/10 rounded-lg text-xs text-white/70 hover:text-amber-400 transition-colors"
                  >
                    <i className={`${selectedEmail.starred ? 'fas fa-star text-amber-400' : 'far fa-star'}`}></i>
                  </button>
                </div>

                <button
                  onClick={() => handleDelete(selectedEmail.id)}
                  className="px-2 py-1 text-white/60 hover:text-red-400 transition-colors text-xs flex items-center gap-1"
                >
                  <i className="fas fa-trash-alt"></i>
                  <span className="hidden sm:inline">Delete</span>
                </button>
              </div>

              {/* Message content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4">
                <div>
                  <h2 className="text-lg font-bold text-white mb-2 leading-snug">
                    {selectedEmail.subject}
                  </h2>
                  <div className="flex items-center justify-between text-xs text-white/60 pb-3 border-b border-white/10">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-pink-500/20 text-pink-300 font-bold flex items-center justify-center text-xs">
                        {selectedEmail.sender[0]}
                      </div>
                      <div>
                        <div className="font-medium text-white">{selectedEmail.sender}</div>
                        <div className="text-[10px] text-white/40">{selectedEmail.email}</div>
                      </div>
                    </div>
                    <span>{selectedEmail.date} at {selectedEmail.time}</span>
                  </div>
                </div>

                <div className="text-xs sm:text-sm text-white/85 leading-relaxed whitespace-pre-line pt-2">
                  {selectedEmail.body}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-white/40 text-xs">
              <i className="fas fa-envelope-open text-3xl mb-2"></i>
              <p>Select a message to view its content</p>
            </div>
          )}
        </div>
      </div>

      {/* Compose modal */}
      {isComposing && (
        <div className="absolute inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl flex flex-col">
            <div className="p-3 bg-white/5 border-b border-white/10 flex items-center justify-between">
              <span className="font-semibold text-xs text-white">New Message</span>
              <button
                onClick={() => setIsComposing(false)}
                className="text-white/60 hover:text-white text-xs"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSend} className="p-4 space-y-3 flex-1 flex flex-col">
              <div>
                <input
                  type="email"
                  placeholder="To: recipient@example.com"
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-pink-400"
                  required
                />
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Subject"
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-pink-400"
                  required
                />
              </div>

              <div className="flex-1">
                <textarea
                  placeholder="Write your message here..."
                  value={composeBody}
                  onChange={(e) => setComposeBody(e.target.value)}
                  rows={8}
                  className="w-full bg-white/5 border border-white/10 rounded-lg p-3 text-xs text-white placeholder-white/40 focus:outline-none focus:ring-1 focus:ring-pink-400 resize-none"
                  required
                ></textarea>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
                <button
                  type="button"
                  onClick={() => setIsComposing(false)}
                  className="px-4 py-1.5 bg-white/10 hover:bg-white/15 rounded-lg text-xs text-white transition-colors"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="px-5 py-1.5 bg-pink-500 hover:bg-pink-600 text-white rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5"
                >
                  <i className="fas fa-paper-plane text-[10px]"></i>
                  Send
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default MailApp